import { Combobox, useFilter, useToastContext } from "@ttc3k/trekker";
import { PersonDetailsQuery } from "gql/generated";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  SelectableTagWithCount,
  usePeopleTagsContext,
} from "shared/components/";
import { usePostHog } from "shared/hooks";

export type UsePersonHeaderCardProps = {
  person: PersonDetailsQuery["personDetails"];
};

export const usePersonHeaderCard = ({ person }: UsePersonHeaderCardProps) => {
  const { t } = useTranslation("people", {
    keyPrefix: "PERSON_DETAILS_MODAL.TAGS.UPDATE",
  });
  const { t: tCore } = useTranslation("core");

  const { toastFactory } = useToastContext();
  const { trackPostHogException } = usePostHog();

  const {
    canAssignTags,
    canManageTags,
    allTagGroupsWithTags,
    membershipTierTags,
    updatingTagsForPersonError,
    updatingTagsForPersonLoading,
    updateTagsForPerson,
  } = usePeopleTagsContext();

  const allTags = useMemo(
    () => [...(allTagGroupsWithTags[0]?.tags ?? []), ...membershipTierTags],
    [allTagGroupsWithTags, membershipTierTags],
  );

  const handleTagsChange = async (tagIds: string[]) => {
    const newTags = await updateTagsForPerson(person, tagIds);
    if (newTags) {
      toastFactory.create({
        title: tCore("SUCCESS"),
        description: t("SUCCESS"),
      });
    } else {
      trackPostHogException(new Error("Error updating tags"), {
        fileName: "usePersonHeaderCard.ts",
        method: "handleTagsChange",
        message: "Error updating tags",
      });
      toastFactory.create({
        title: tCore("ERROR.TITLE"),
        description: t("ERROR"),
      });
    }
  };

  useEffect(() => {
    if (updatingTagsForPersonError) {
      trackPostHogException(new Error("Error updating tags"), {
        fileName: "usePersonHeaderCard.ts",
        method: "handleTagsChange",
        message: "Error updating tags",
      });
      toastFactory.create({
        title: tCore("ERROR.TITLE"),
        description: t("ERROR"),
      });
    }
  }, [
    t,
    tCore,
    toastFactory,
    updatingTagsForPersonError,
    trackPostHogException,
  ]);

  const { contains } = useFilter({ sensitivity: "base" });

  const { collection, filter, set } = Combobox.useCollection({
    initialItems: [] as SelectableTagWithCount[],
    filter: contains,
  });

  useEffect(() => {
    set(allTags);
  }, [allTags, person?.personInfo?.tags, set]);

  /**
   * Filter tags based on the input value
   * @param search - The input value to filter the tags
   */
  const handleTagInputValueSearch = (search: string) => {
    filter(search);
  };

  const handleComboboxOpenChange = (open: boolean) => {
    if (open) {
      set(allTags);
    }
  };

  return {
    canAssignTags,
    canManageTags,
    updatingTagsForPersonLoading,
    handleTagsChange,
    filterableTagsCollection: collection,
    handleTagInputValueSearch,
    handleComboboxOpenChange,
  };
};
